import React from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Spin, message, Popconfirm, Modal } from 'antd';
import { ajax } from '../utils/request';
import './chat.css';
import '../work/index.css';
import { useTranslation } from "react-i18next"
import { getToken } from "../utils/storage";
import { marked } from 'marked';

import NewChatButton from "../components/newchatbutton";
import { MsgSVG, Trash, Send, Download } from "../components/icons";
import FAQ from "../components/faq";

// Reusing the Hamburger Icon from your other files
const HamburgerIcon = ({ open }) => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    {open ? (
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round" />
    ) : (
      <path d="M3 6H21M3 12H21M3 18H21" stroke="black" strokeWidth="2" strokeLinecap="round" />
    )}
  </svg>
);

export default function CiviRender() {
  const navigate = useNavigate();
  const { chatId } = useParams();
  const { t } = useTranslation();
  const [messageApi, contextHolder] = message.useMessage();

  // UI States
  const [loading, setLoading] = React.useState(false);
  const [sidebarLoading, setSidebarLoading] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(true);
  const [faqOpen, setFaqOpen] = React.useState(false);

  // Data States
  const [question, setQuestion] = React.useState(''); // For initial upload
  const [followup, setFollowup] = React.useState(''); // For chat continuation
  const [file, setFile] = React.useState(null);
  const [messages, setMessages] = React.useState([]);
  const [chats, setChats] = React.useState([]);

  // Refs & Layout
  const chatScrollRef = React.useRef(null);
  const textAreaRef = React.useRef(null);
  const [windowHeight, setWindowHeight] = React.useState(window.innerHeight);
  const { confirm } = Modal;
  const [count, setCount] = React.useState(1); // Trigger for auth check

  // Function to render markdown messages
  const getRenderedMessage = (messageContent) => {
    if (typeof messageContent !== 'string') {
      return '';
    }
    return marked.parse(messageContent);
  };

  // Auto-resize textarea logic
  const resizeTextArea = React.useCallback(() => {
    if (textAreaRef.current) {
      textAreaRef.current.style.height = "auto";
      textAreaRef.current.style.height = textAreaRef.current.scrollHeight + "px";
      if (textAreaRef.current.scrollHeight >= 230) {
        textAreaRef.current.style.overflowY = "overlay";
      } else {
        textAreaRef.current.style.overflowY = "hidden";
      }
    }
  }, []);

  // Scroll to bottom when messages change
  React.useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTo({
        top: chatScrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [messages, sending]);

  // Handle textarea resize on input change
  const currentInput = chatId ? followup : question;
  React.useEffect(resizeTextArea, [currentInput, resizeTextArea]);

  // Window resize effect
  React.useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // --- API Calls ---

  const fetchChats = React.useCallback(async () => {
    const token = await getToken();
    if (!token) return;

    try {
      setSidebarLoading(true);
      // Changed endpoint to /render/chats
      const res = await ajax.get('/render/chats');
      setChats(res?.data?.data || []);
    } catch (e) {
      console.error(e);
      messageApi.error("Failed to load CiviRender chat history.");
    } finally {
      setSidebarLoading(false);
    }
  }, [messageApi]);

  const fetchChatById = React.useCallback(async (id) => {
    if (!id) {
      setMessages([]);
      return;
    };
    try {
      setLoading(true);
      // Changed endpoint to /render/chats/{id}
      const res = await ajax.get(`/render/chats/${id}`);
      const data = res?.data?.data;

      // Map response to state, including image_url
      setMessages((data?.messages || []).map(m => ({
        role: m.role === 'user' ? 'user' : 'ai',
        content: m.content,
        image_url: m.image_url // Store the signed URL
      })));
    } catch {
      messageApi.error('Unable to load this chat.');
      navigate('/render/start');
    } finally {
      setLoading(false);
    }
  }, [messageApi, navigate]);

  React.useEffect(() => { fetchChats(); }, [fetchChats]);
  React.useEffect(() => { fetchChatById(chatId); }, [chatId, fetchChatById]);

  // User auth check
  React.useEffect(() => {
    getToken().then((token) => {
      if (token) {
        ajax.get(`/user/me`).catch(error => {
          if (error.response && error.response.status === 403) {
            showConfirm();
          }
        });
      } else {
        showConfirm();
      }
    });
  }, [count]);

  // --- Handlers ---

  const onChooseFile = (e) => {
    const f = e.target.files?.[0];
    // Changed to accept images
    if (f && !f.type.startsWith('image/')) {
      messageApi.warning('Please select an image file (PNG, JPG, JPEG).');
      return;
    }
    if (f && f.size > 25 * 1024 * 1024) { // 25MB limit
      messageApi.warning('File is too large (max 25MB).');
      return;
    }
    setFile(f || null);
  };

  const startConversation = async () => {
    //if (!file) return messageApi.warning('Please choose an image.');
    const questionToAsk = question.trim();
    if (!questionToAsk) return messageApi.warning('Please write a prompt.');

    try {
      setSending(true);

      setMessages([{ role: 'user', content: questionToAsk }]);
      setQuestion('');

      const fd = new FormData();
      if (file) { fd.append('file', file); }
      fd.append('question', questionToAsk);

      // Changed endpoint to /render/upload
      const res = await ajax.post('/render/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { chat_id, messages: responseMessages } = res?.data?.data || {};

      // Update with the full message history returned by server (contains signed URLs)
      if (responseMessages) {
        setMessages(responseMessages.map(m => ({
          role: m.role === 'user' ? 'user' : 'ai',
          content: m.content,
          image_url: m.image_url
        })));
      }

      setFile(null);
      await fetchChats();
      if (chat_id) navigate(`/render/${chat_id}`);

    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Upload failed');
      setMessages([]);
    } finally {
      setSending(false);
    }
  };

  const sendFollowup = async () => {
    if (!chatId) return messageApi.warning('Please open a chat.');
    const questionToAsk = followup.trim();
    if (!questionToAsk) return;

    let prevMessages = [...messages];

    try {
      setSending(true);
      // Optimistic update for text
      const tempMsg = { role: 'user', content: questionToAsk, image_url: null };
      setMessages([...prevMessages, tempMsg]);
      setFollowup('');

      // Changed endpoint to /render/chat
      const res = await ajax.post('/render/chat', {
        chat_id: Number(chatId),
        question: questionToAsk
      });

      const { messages: updatedMessages } = res?.data?.data || {};

      if (updatedMessages) {
        setMessages(updatedMessages.map(m => ({
          role: m.role === 'user' ? 'user' : 'ai',
          content: m.content,
          image_url: m.image_url
        })));
      }
    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Failed to generate response.');
      setMessages(prevMessages); // Revert on error
    } finally {
      setSending(false);
    }
  };

  const sendMessage = () => {
    if (chatId) {
      sendFollowup();
    } else {
      startConversation();
    }
  };

  const handleEnterKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !sending) {
      e.preventDefault();
      sendMessage();
    }
  };

  const showConfirm = () => {
    confirm({
      title: t("book.tipTitle"),
      content: t("book.tipContent"),
      onOk() { window.location.href = "/"; },
      onCancel() { window.location.href = "/"; },
    });
  };

  const handleDeleteChat = async (id) => {
    try {
      // Changed endpoint to /render/chats/{id}
      await ajax.delete(`/render/chats/${id}`);
      messageApi.success('Chat deleted');
      if (String(id) === String(chatId)) {
        navigate('/render/start');
        setMessages([]);
      }
      fetchChats();
    } catch (e) {
      console.error(e);
      messageApi.error(e?.response?.data?.detail || 'Deletion failed');
    }
  };

  const handleNewChat = () => navigate('/render/start');

  // Controls whether we show the chat interface or the upload interface
  const showOverlay = !chatId;

  const handleDownload = async (imageUrl) => {
    try {
      messageApi.open({
        type: 'loading',
        content: 'Downloading...',
        duration: 0,
        key: 'downloading',
      });

      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `civirender-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
      messageApi.destroy('downloading');
      messageApi.success('Image downloaded');
    } catch (error) {
      console.error(error);
      messageApi.destroy('downloading');
      messageApi.error('Download failed. Opening in new tab.');
      window.open(imageUrl, '_blank');
    }
  };

  return (
    <div className="main h-full w-full">
      {contextHolder}

      <div className="wrapper flex flex-row">
        <FAQ isOpen={faqOpen} setOpen={setFaqOpen} />

        {/* SIDEBAR - Identical structure to ChatPdf, pointing to Render routes */}
        <div
          className={`sider flex flex-col bg-[#f5f5f7] transition-all duration-300`}
          style={{ width: sidebarOpen ? "256px" : "60px", height: windowHeight }}
        >
          {/* Header */}
          <div className="sider-header flex items-center justify-between mx-[8px] my-[8px]">
            {sidebarOpen && (
              <a href="/" className="sider-title flex items-center gap-2 font-bold text-black hover:opacity-80 transition-opacity">
                <img src="/images/Logo CiviRender.png" alt="CiviRender" className="h-[45px]" />
              </a>
            )}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="toggle-sidebar p-2 rounded-md hover:bg-gray-200"
            >
              <HamburgerIcon open={sidebarOpen} />
            </button>
          </div>

          {/* NewChatButton */}
          {sidebarOpen ? (
            <NewChatButton onNewChat={handleNewChat} />
          ) : (
            <div className="flex justify-center mx-[8px] mb-[8px]">
              <button
                onClick={handleNewChat}
                className="p-2 rounded-md hover:bg-gray-200 transition-colors"
                title="New Chat"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>
          )}

          {/* Chat List */}
          {sidebarOpen && (
            <div className="sider-scroll-area">
              <div className="pdf-list flex flex-col my-[8px]">
                {chats && chats.map((item) => {
                  const isActive = String(item.id) === String(chatId);
                  const chatDate = new Date(item.update_time).toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  });

                  return (
                    <Link
                      key={item.id}
                      to={`/render/${item.id}`}
                      className={`${isActive ? "active" : ""} chat-item rounded-md text-[14px] flex flex-row items-center h-[40px] flex-grow group`}
                    >
                      <span>
                        <MsgSVG fill={`${isActive ? '#fff' : "#000"}`} size="18" />
                      </span>
                      <span className="whitespace-nowrap text-ellipsis overflow-hidden ml-[5px]">
                        {item.title}
                      </span>
                      <span className="ml-auto text-[12px] opacity-70">
                        {chatDate}
                      </span>
                      <Popconfirm
                        title="Delete the chat"
                        description="Are you sure to delete this chat?"
                        onConfirm={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteChat(item.id);
                        }}
                        okText="Yes"
                        cancelText="No"
                      >
                        <button
                          className="delete-chat-button opacity-0 group-hover:opacity-100 transition-opacity ml-2"
                          style={{ background: 'none', border: 'none', padding: 0 }}
                        >
                          <Trash size={18} fill="#969696" />
                        </button>
                      </Popconfirm>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer */}
          {sidebarOpen ? (
            <div className="sider-footer text-[#969696] flex gap-[9px] px-[8px] text-[14px] hover:text-[var(--blue-text)] cursor-pointer">
              <a className="text-[#969696] text-[14px]" href="/">
                {t("index")}
              </a>
              <div
                className="text-[#969696] hover:text-[var(--blue-text)] transition cursor-pointer"
                onClick={() => setFaqOpen(true)}
              >
                FAQ
              </div>
            </div>
          ) : (
            <div className="sider-footer-closed flex justify-center items-center pb-3">
              <a href="/" className="sidebar-mini-logo">
                <img src="/images/civirender.png" alt="CiviBOT" className="h-[40px] w-auto opacity-80 hover:opacity-100 transition" />
              </a>
            </div>
          )}
        </div>

        {/* CONTENT AREA - Full width, no PDF split */}
        <div
          className="content flex flex-row transition-all duration-300"
          style={{ width: sidebarOpen ? "calc(100vw - 256px)" : "calc(100vw - 60px)" }}
        >
          <div className="flex-1 bg-white flex justify-center">
            <div className="chat-wrapper w-full max-w-full flex flex-col" style={{ height: windowHeight }}>

              {/* Chat Messages Area */}
              <div className="chat-list-items flex-grow-1 flex justify-center overflow-auto" ref={chatScrollRef}>
                <div className="w-full max-w-[720px]">
                  <div className="px-4 py-2">

                    {loading && <div className="chat-message-item ai"><div className="chat-message"><Spin size="small" /></div></div>}

                    {/* Welcome / Start Screen */}
                    {showOverlay && !loading && (
                      <div className="chat-message-item welcome">
                        <div className="chat-message" style={{ padding: '20px 30px', background: '#fff', borderRadius: '12px', maxWidth: '720px', margin: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.05)' }}>
                          <div className="flex justify-center items-center gap-4 mb-4">
                            <img src="/images/civirender.png" alt="CiviBOT" style={{ height: '32px' }} />
                            <h1 style={{ fontSize: '24px', margin: 0 }}>
                              Civi<span style={{ color: '#793aa3ff' }}>Render</span>
                            </h1>
                            <img src="/images/civirender.png" alt="CiviBOT" style={{ height: '32px' }} />
                          </div>

                          <p style={{ color: '#555', marginBottom: '16px', textAlign: 'center' }}>
                            Upload an image and use AI to generate or edit content.
                          </p>

                          <h2 style={{ fontSize: '18px', marginBottom: '16px' }}>📌 How to Use:</h2>

                          <div style={{ textAlign: 'left', fontSize: '15px', lineHeight: '1.7' }}>
                            <p>🎨 <strong>Prompt –</strong> Describe what you want to create.</p>
                            <p>🖼️ <strong>Reference (Optional) –</strong> Upload an image if you want to edit it.</p>
                            <p>✨ <strong>Generate –</strong> CiviRender will create an image based on your request.</p>
                          </div>

                          <div style={{ marginTop: '30px', display: 'flex', gap: '20px', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}>
                            <input
                              type="file"
                              id="image-upload-input"
                              accept="image/png, image/jpeg, image/jpg"
                              onChange={onChooseFile}
                              style={{ display: 'none' }}
                              disabled={sending}
                            />
                            <button
                              className="dropdown"
                              style={{
                                fontWeight: '600',
                                cursor: 'pointer',
                                border: '1px solid #1677ff',
                                color: '#1677ff',
                                background: '#e6f4ff'
                              }}
                              disabled={sending}
                              onClick={() => document.getElementById('image-upload-input').click()}
                            >
                              🖼️ Upload Image
                            </button>
                            {file && <p style={{ marginTop: '10px', color: '#1677ff', textAlign: 'center', fontSize: '14px' }}>File: {file.name}</p>}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Active Chat Loop */}
                    {!loading && messages.map((item, index) => (
                      <div key={index} className={`chat-message-item ${item.role === 'user' ? 'human' : 'ai'}`}>
                        <div className="chat-message" >
                          {/* 1. Render Text content */}
                          <div dangerouslySetInnerHTML={{
                            __html: item.role === 'ai'
                              ? `<div class="markdown-content">${getRenderedMessage(item.content)}</div>`
                              : item.content
                          }}>
                          </div>

                          {/* 2. Render Image if available */}
                          {item.image_url && (
                            <div className="mt-3 inline-block relative group">
                              <img
                                src={item.image_url}
                                alt="Rendered content"
                                className="rounded-lg max-w-full h-auto border border-gray-200"
                                style={{ maxHeight: '400px' }}
                              />

                              {/* Download Button */}
                              <button
                                onClick={() => handleDownload(item.image_url)}
                                className="absolute top-2 right-2 p-1.5 flex items-center justify-center bg-white/90 rounded-md border-none outline-none shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-white cursor-pointer"
                                title="Download Image"
                                type="button"
                              >
                                <Download size={18} fill="#4b5563" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}

                    {sending && (
                      <div className="chat-message-item ai">
                        <div className="chat-message">
                          <Spin size="small" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Input Area */}
              <div className={`chat-input-wrapper px-4 ${showOverlay ? "mt-6 mb-10 flex flex-col items-center" : "mt-auto"}`}>
                {showOverlay && (
                  <div className="text-black text-[18px] font-semibold mb-2 text-center">
                    Describe your creation
                  </div>
                )}

                <div
                  className={`send-container w-full max-w-[720px] ${showOverlay ? "" : "mx-auto"}`}
                  onKeyDown={handleEnterKey}
                >
                  <textarea
                    placeholder={showOverlay ? "Describe the changes..." : "Continue editing this image..."}
                    className="message-input"
                    rows={1}
                    ref={textAreaRef}
                    value={chatId ? followup : question}
                    onChange={(e) => chatId ? setFollowup(e.target.value) : setQuestion(e.target.value)}
                    disabled={sending && showOverlay}
                  ></textarea>
                  <button
                    className="message-send"
                    style={{ background: sending ? 'rgb(187 189 193)' : '#1677ff' }}
                    onClick={sendMessage}
                    disabled={sending}
                  >
                    <span className="flex flex-col items-center text-center text-[14px]">
                      <Send size={14} />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}